import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Space,
  Divider,
  Collapse,
  Row,
  Col,
} from 'antd';
import './AddWidgetModal.scss';

const { Option } = Select;
const { Panel } = Collapse;

const AddWidgetModal = ({ 
  visible, 
  onClose, 
  onSave, 
  sensores = [], 
  loadingSensores = false,
  editWidget = null // Nuevo: widget a editar (null = modo crear)
}) => {
  const [form] = Form.useForm();
  const [widgetTipo, setWidgetTipo] = useState('gauge');
  const [tipoSensor, setTipoSensor] = useState(null); // 'consumo' o 'no-consumo'
  const [sensoresFiltrados, setSensoresFiltrados] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editWidget;

  // Detectar si un sensor es de consumo
  const esSensorConsumo = (sensor) => {
    if (sensor.consumo === true) return true;
    const unidadesConsumo = ['L','l','M3','m3','KWh','kWh','kwh','Wh','wh'];
    return unidadesConsumo.includes(sensor.unidades);
  };

  // Filtrar sensores cuando cambia el tipo seleccionado
  useEffect(() => {
    if (tipoSensor === 'consumo') {
      setSensoresFiltrados(sensores.filter(s => esSensorConsumo(s)));
    } else if (tipoSensor === 'no-consumo') {
      setSensoresFiltrados(sensores.filter(s => !esSensorConsumo(s)));
    } else {
      setSensoresFiltrados([]);
    }
  }, [tipoSensor, sensores]);

  // Cargar valores del widget al abrir en modo edición
  useEffect(() => {
    if (editWidget && visible) {
      setWidgetTipo(editWidget.tipo);
      
      // Detectar tipo de sensor en modo edición
      const sensorId = editWidget.config.sensorId || editWidget.config.sensores?.[0];
      if (sensorId) {
        const sensor = sensores.find(s => s._id === sensorId);
        if (sensor) {
          setTipoSensor(esSensorConsumo(sensor) ? 'consumo' : 'no-consumo');
        }
      }
      
      // Prellenar el formulario con los valores del widget
      const formValues = {
        titulo: editWidget.config.titulo,
        autoRefresh: editWidget.config.autoRefresh || 60,
        ...editWidget.config
      };

      form.setFieldsValue(formValues);
    } else if (!visible) {
      // Resetear TODO cuando se cierra
      form.resetFields();
      setWidgetTipo(null);
      setTipoSensor(null);
      setSensoresFiltrados([]);
    } else if (visible && !editWidget) {
      // Resetear cuando se abre en modo crear (NUEVO)
      form.resetFields();
      setWidgetTipo(null);
      setTipoSensor(null);
      setSensoresFiltrados([]);
    }
  }, [editWidget, visible, form, sensores]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (isEditMode) {
        // Modo edición: solo enviar config actualizado
        const updateData = {
          config: buildConfig(widgetTipo, values),
        };
        await onSave(editWidget.widgetId, updateData);
      } else {
        // Modo creación: enviar widget completo
        const widgetData = {
          tipo: widgetTipo,
          x: 0,
          y: 0,
          w: getDefaultWidth(widgetTipo),
          h: getDefaultHeight(widgetTipo),
          config: buildConfig(widgetTipo, values),
        };
        await onSave(widgetData);
      }

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Error al validar formulario:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultWidth = (tipo) => {
    switch (tipo) {
      case 'timeseries':
        return 6;
      case 'gauge':
        return 3;
      case 'kpi':
        return 3;
      default:
        return 4;
    }
  };

  const getDefaultHeight = (tipo) => {
    switch (tipo) {
      case 'timeseries':
        return 4;
      case 'gauge':
        return 4;
      case 'kpi':
        return 3;
      default:
        return 4;
    }
  };

  const buildConfig = (tipo, values) => {
    const baseConfig = {
      titulo: values.titulo,
      autoRefresh: values.autoRefresh || 60,
    };

    switch (tipo) {
      case 'timeseries':
        return {
          ...baseConfig,
          sensores: values.sensores || [],
          ventanaTemporal: values.ventanaTemporal || '24h',
          // Removido: agregacion (ya no se necesita)
        };

      case 'gauge': {
        // Formato simplificado según GAUGE_WIDGET_UMBRALES_SIMPLIFICADOS.md
        // Los umbrales se definen directamente en config, no en config.umbrales
        // El backend calculará automáticamente los rangos verde/amarillo/rojo
        
        // Asegurar valores válidos por defecto
        const minAceptable = values.minAceptable !== null && values.minAceptable !== undefined 
          ? Number(values.minAceptable) 
          : 20;
        const maxAceptable = values.maxAceptable !== null && values.maxAceptable !== undefined 
          ? Number(values.maxAceptable) 
          : 80;
        const tolerancia = values.tolerancia !== null && values.tolerancia !== undefined 
          ? Number(values.tolerancia) 
          : 10;
        
        // Validación adicional: asegurar que min < max
        if (minAceptable >= maxAceptable) {
          throw new Error('El valor mínimo aceptable debe ser menor que el máximo');
        }
        
        return {
          ...baseConfig,
          sensorId: values.sensorId,
          minAceptable,
          maxAceptable,
          tolerancia,
          diseno: values.diseno || 'circular', // Diseño visual del gauge
        };
      }

      case 'kpi':
        return {
          ...baseConfig,
          sensorId: values.sensorId,
          ventanaComparacion: values.ventanaComparacion || '7d',
          // Removido: agregacion (ya no se necesita, backend calcula automáticamente)
          formato: {
            decimales: values.decimales || 2,
            prefijo: values.prefijo || '',
            sufijo: values.sufijo || '',
          },
        };

      default:
        return baseConfig;
    }
  };

  const tipoWidgetInfo = {
    timeseries: {
      titulo: 'Serie de Tiempo',
      descripcion: 'Gráfica de línea para visualizar tendencias. Soporta múltiples sensores.',
      permitido: ['consumo', 'no-consumo'], // Ambos tipos
    },
    gauge: {
      titulo: 'Medidor',
      descripcion: 'Indicador con umbrales de color. SOLO para sensores NO de consumo (temperatura, presión, etc.).',
      permitido: ['no-consumo'], // Solo NO consumo
    },
    kpi: {
      titulo: 'KPI',
      descripcion: 'Indicador de consumo de HOY vs histórico. SOLO para sensores de consumo (L, M3, KWh).',
      permitido: ['consumo'], // Solo consumo
    },
  };

  // Obtener tipos de widgets permitidos según el tipo de sensor seleccionado
  const getWidgetsPermitidos = () => {
    if (!tipoSensor) return [];
    return Object.keys(tipoWidgetInfo).filter(key => 
      tipoWidgetInfo[key].permitido.includes(tipoSensor)
    );
  };



  return (
    <Modal
      title={isEditMode ? "Editar Widget" : "Agregar Widget al Dashboard"}
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={650}
      okText={isEditMode ? "Guardar Cambios" : "Agregar Widget"}
      cancelText="Cancelar"
      className="add-widget-modal"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          autoRefresh: 60,
          ventanaTemporal: '24h',
          ventanaComparacion: '7d',
          decimales: 2,
          minAceptable: 20,
          maxAceptable: 80,
          tolerancia: 10,
          diseno: 'circular',
        }}
      >
        {/* Paso 1: Seleccionar tipo de sensor (solo en modo crear) */}
        {!isEditMode && (
          <Form.Item
            label="1. ¿Qué tipo de sensor quieres visualizar?"
            extra="Los sensores de consumo acumulan valores (agua, gas, electricidad). Los sensores NO de consumo miden valores instantáneos (temperatura, presión, etc.)."
          >
            <Select
              value={tipoSensor}
              onChange={(value) => {
                setTipoSensor(value);
                setWidgetTipo(null);
                form.resetFields(['sensores', 'sensorId', 'tipo']);
              }}
              placeholder="Selecciona el tipo de sensor"
              size="large"
            >
              <Option value="no-consumo">
                <strong>Sensores NO de Consumo</strong>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  Temperatura, Presión, Humedad, Nivel, etc.
                </div>
              </Option>
              <Option value="consumo">
                <strong>Sensores de Consumo (Acumulativos)</strong>
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  Agua (L), Gas (M3), Electricidad (KWh)
                </div>
              </Option>
            </Select>
          </Form.Item>
        )}

        {/* Paso 2: Seleccionar tipo de widget (solo widgets permitidos) */}
        {!isEditMode && tipoSensor && (
          <Form.Item
            name="tipo"
            label="2. Selecciona el tipo de widget"
            extra={widgetTipo ? tipoWidgetInfo[widgetTipo]?.descripcion : 'Elige cómo quieres visualizar los datos'}
          >
            <Select
              value={widgetTipo}
              onChange={setWidgetTipo}
              size="large"
              placeholder="Selecciona un tipo de widget"
            >
              {getWidgetsPermitidos().map(key => (
                <Option key={key} value={key}>
                  {tipoWidgetInfo[key].titulo}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {isEditMode && (
          <div style={{ marginBottom: 16, padding: '12px', background: '#f5f5f5', borderRadius: '4px' }}>
            <strong>Tipo de sensor:</strong> {tipoSensor === 'consumo' ? 'Consumo' : 'NO Consumo'} • 
            <strong> Tipo de widget:</strong> {tipoWidgetInfo[widgetTipo]?.titulo}
          </div>
        )}

        {/* Paso 3: Configuración Básica (solo si ya seleccionó tipo de widget) */}
        {widgetTipo && (
          <>
            <Form.Item
              name="titulo"
              label={isEditMode ? "Título del Widget" : "3. Título del Widget"}
              rules={[{ required: true, message: 'El título es obligatorio' }]}
            >
              <Input placeholder="Ej: Temperatura Ambiente" size="large" />
            </Form.Item>

            {/* Selector de sensores según tipo */}
            {widgetTipo === 'timeseries' ? (
              <Form.Item
                name="sensores"
                label={isEditMode ? "Sensores" : "4. Sensores"}
                rules={[{ required: true, message: 'Selecciona al menos un sensor' }]}
                extra="Puedes seleccionar múltiples sensores para comparar"
              >
                <Select
                  mode="multiple"
                  placeholder="Busca y selecciona sensores"
                  loading={loadingSensores}
                  disabled={loadingSensores || sensoresFiltrados.length === 0}
                  showSearch
                  filterOption={(input, option) => {
                    const sensor = sensoresFiltrados.find(s => s._id === option.value);
                    if (!sensor) return false;
                    const searchText = `${sensor.nombre} ${sensor.alias || ''}`.toLowerCase();
                    return searchText.includes(input.toLowerCase());
                  }}
                  size="large"
                  maxTagCount={3}
                >
                  {sensoresFiltrados.map((sensor) => (
                    <Option key={sensor._id} value={sensor._id}>
                      {sensor.nombre} {sensor.alias ? `(${sensor.alias})` : ''} - {sensor.unidades}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            ) : (
              <Form.Item
                name="sensorId"
                label={isEditMode ? "Sensor" : "4. Sensor"}
                rules={[{ required: true, message: 'Selecciona un sensor' }]}
              >
                <Select
                  placeholder="Busca y selecciona un sensor"
                  loading={loadingSensores}
                  disabled={loadingSensores || sensoresFiltrados.length === 0}
                  showSearch
                  filterOption={(input, option) => {
                    const sensor = sensoresFiltrados.find(s => s._id === option.value);
                    if (!sensor) return false;
                    const searchText = `${sensor.nombre} ${sensor.alias || ''}`.toLowerCase();
                    return searchText.includes(input.toLowerCase());
                  }}
                  size="large"
                >
                  {sensoresFiltrados.map((sensor) => (
                    <Option key={sensor._id} value={sensor._id}>
                      {sensor.nombre} {sensor.alias ? `(${sensor.alias})` : ''} - {sensor.unidades}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </>
        )}

        {/* Configuración Avanzada */}
        {widgetTipo && (
          <Collapse
            defaultActiveKey={[]}
            style={{ marginBottom: 16 }}
            expandIconPosition="end"
          >
            <Panel header="Configuración Avanzada (Opcional)" key="1">
              {/* Time-series específico */}
              {widgetTipo === 'timeseries' && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Form.Item name="ventanaTemporal" label="Ventana de Tiempo">
                    <Select>
                      <Option value="1h">1 hora</Option>
                      <Option value="6h">6 horas</Option>
                      <Option value="12h">12 horas</Option>
                      <Option value="24h">24 horas</Option>
                      <Option value="7d">7 días</Option>
                      <Option value="30d">30 días</Option>
                    </Select>
                  </Form.Item>
                </Space>
              )}

              {/* Gauge específico */}
              {widgetTipo === 'gauge' && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Form.Item 
                    name="diseno" 
                    label="Diseño Visual"
                    rules={[{ required: true, message: 'Selecciona un diseño' }]}
                    initialValue="circular"
                  >
                    <Select placeholder="Selecciona el diseño">
                      <Option value="circular">Circular - Gauge completo (360°)</Option>
                      <Option value="semicircular">Semicircular - Medio círculo (180°)</Option>
                      <Option value="vertical">Vertical - Barra/Tanque vertical</Option>
                      <Option value="horizontal">Horizontal - Barra de progreso</Option>
                    </Select>
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item 
                        name="minAceptable" 
                        label="Mínimo Aceptable"
                        rules={[
                          { required: true, message: 'Ingresa el mínimo' },
                          { type: 'number', message: 'Debe ser un número' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const maxAceptable = getFieldValue('maxAceptable');
                              if (value === null || value === undefined) {
                                return Promise.reject(new Error('El valor mínimo es requerido'));
                              }
                              if (maxAceptable !== null && maxAceptable !== undefined && value >= maxAceptable) {
                                return Promise.reject(new Error('Debe ser menor que el máximo'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                        initialValue={20}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="Ej: 20"
                          onChange={() => form.validateFields(['maxAceptable'])}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item 
                        name="maxAceptable" 
                        label="Máximo Aceptable"
                        rules={[
                          { required: true, message: 'Ingresa el máximo' },
                          { type: 'number', message: 'Debe ser un número' },
                          ({ getFieldValue }) => ({
                            validator(_, value) {
                              const minAceptable = getFieldValue('minAceptable');
                              if (value === null || value === undefined) {
                                return Promise.reject(new Error('El valor máximo es requerido'));
                              }
                              if (minAceptable !== null && minAceptable !== undefined && value <= minAceptable) {
                                return Promise.reject(new Error('Debe ser mayor que el mínimo'));
                              }
                              return Promise.resolve();
                            },
                          }),
                        ]}
                        initialValue={80}
                      >
                        <InputNumber 
                          style={{ width: '100%' }} 
                          placeholder="Ej: 80"
                          onChange={() => form.validateFields(['minAceptable'])}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item 
                    name="tolerancia" 
                    label="Tolerancia (%)"
                    rules={[{ required: true, message: 'Selecciona la tolerancia' }]}
                    tooltip="Porcentaje de margen fuera del rango aceptable que se considera como 'precaución' (amarillo) antes de llegar a peligro (rojo)"
                    initialValue={10}
                  >
                    <Select placeholder="Selecciona tolerancia">
                      <Option value={5}>5% - Muy estricto</Option>
                      <Option value={10}>10% - Estricto (recomendado)</Option>
                      <Option value={25}>25% - Moderado</Option>
                      <Option value={50}>50% - Flexible</Option>
                      <Option value={75}>75% - Muy flexible</Option>
                      <Option value={100}>100% - Permisivo</Option>
                    </Select>
                  </Form.Item>
                </Space>
              )}

              {/* KPI específico */}
              {widgetTipo === 'kpi' && (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Form.Item name="ventanaComparacion" label="Comparar con período histórico">
                    <Select>
                      <Option value="1d">Ayer (1 día)</Option>
                      <Option value="7d">Últimos 7 días</Option>
                      <Option value="15d">Últimos 15 días</Option>
                      <Option value="30d">Últimos 30 días</Option>
                    </Select>
                  </Form.Item>

                  <Divider plain>Formato de Número</Divider>

                  <Form.Item name="decimales" label="Decimales">
                    <InputNumber min={0} max={4} style={{ width: '100%' }} />
                  </Form.Item>

                  <Space.Compact style={{ width: '100%' }}>
                    <Form.Item name="prefijo" label="Prefijo" style={{ width: '50%', marginBottom: 0 }}>
                      <Input placeholder="$ o €" />
                    </Form.Item>
                    <Form.Item name="sufijo" label="Sufijo" style={{ width: '50%', marginBottom: 0 }}>
                      <Input placeholder="KWh, USD, etc." />
                    </Form.Item>
                  </Space.Compact>
                </Space>
              )}

              <Divider />

              <Form.Item
                name="autoRefresh"
                label="Auto-actualización (segundos)"
                extra="0 = sin actualización automática"
              >
                <InputNumber min={0} max={3600} step={30} style={{ width: '100%' }} />
              </Form.Item>
            </Panel>
          </Collapse>
        )}
      </Form>
    </Modal>
  );
};

AddWidgetModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  sensores: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      nombre: PropTypes.string.isRequired,
    })
  ),
  loadingSensores: PropTypes.bool,
  editWidget: PropTypes.shape({
    widgetId: PropTypes.string.isRequired,
    tipo: PropTypes.oneOf(['gauge', 'kpi', 'timeseries']).isRequired,
    config: PropTypes.object.isRequired,
  }),
};

export default AddWidgetModal;
